import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { SidebarRail, SidebarCollapseButton } from './SidebarToggle.tsx';

interface OperatingRoom {
  operatingRoomId: string;
  roomNumber: string;
  name: string;
  hospitalId: string;
  credential?: string;
}

interface OperatingRoomSchedule {
  scheduleId: string;
  operatingRoomId: string;
  startTimeUtc: string;
  endTimeUtc: string;
  surgeryType: string;
  status: string;
}

interface Hospital {
  hospitalId: string;
  name: string;
}

interface HospitalORSidebarProps {
  hospitals: Hospital[];
  operatingRoomsByHospital: Record<string, OperatingRoom[]>;
  getSchedulesForRoom: (operatingRoomId: string) => OperatingRoomSchedule[];
  getHospitalName: (hospitalId: string) => string;
  onScrollToRoom: (operatingRoomId: string) => void;
  onCreateSchedule: (operatingRoomId: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  viewMode?: 'active' | 'all';
  forcedActiveHospitalIds?: ReadonlySet<string>;
  onToggleForcedHospital?: (hospitalId: string) => void;
}

const HospitalORSidebar: React.FC<HospitalORSidebarProps> = ({
  hospitals,
  operatingRoomsByHospital,
  getSchedulesForRoom,
  getHospitalName,
  onScrollToRoom,
  onCreateSchedule,
  isOpen,
  onToggle,
  viewMode = 'all',
  forcedActiveHospitalIds,
  onToggleForcedHospital,
}) => {
  const { isAdmin } = useAuth();
  const [collapsedHospitals, setCollapsedHospitals] = useState<Set<string>>(() => new Set(Object.keys(operatingRoomsByHospital)));
  const [searchQuery, setSearchQuery] = useState('');

  // Collapse any newly-loaded hospitals that aren't already tracked
  useEffect(() => {
    const hospitalIds = Object.keys(operatingRoomsByHospital);
    if (hospitalIds.length === 0) return;
    setCollapsedHospitals(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const id of hospitalIds) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [operatingRoomsByHospital]);

  const toggleHospital = (hospitalId: string) => {
    setCollapsedHospitals(prev => {
      const next = new Set(prev);
      if (next.has(hospitalId)) {
        next.delete(hospitalId);
      } else {
        next.add(hospitalId);
      }
      return next;
    });
  };

  const filteredHospitalEntries = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return Object.entries(operatingRoomsByHospital)
      .map(([hospitalId, rooms]) => {
        if (!query) return { hospitalId, rooms };
        const hospitalName = getHospitalName(hospitalId).toLowerCase();
        const hospitalMatches = hospitalName.includes(query);
        const matchingRooms = rooms.filter(
          r => (r.name || '').toLowerCase().includes(query) || (r.roomNumber || '').toLowerCase().includes(query)
        );
        if (hospitalMatches) return { hospitalId, rooms };
        if (matchingRooms.length > 0) return { hospitalId, rooms: matchingRooms };
        return null;
      })
      .filter((entry): entry is { hospitalId: string; rooms: OperatingRoom[] } => entry !== null);
  }, [operatingRoomsByHospital, searchQuery, getHospitalName]);

  if (!isOpen) {
    return <SidebarRail label="Sites" side="left" onExpand={onToggle} />;
  }

  return (
    <div className="hospital-or-sidebar">
      <div className="sidebar-header">
        <h3>Sites</h3>
        <SidebarCollapseButton label="Sites" side="left" onCollapse={onToggle} />
      </div>

      <div className="sidebar-search">
        <input
          type="text"
          className="form-control"
          placeholder="Search rooms..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="sidebar-tree">
        {filteredHospitalEntries.length === 0 && (
          <p className="sidebar-empty">No matching hospitals or rooms.</p>
        )}
        {filteredHospitalEntries.map(({ hospitalId, rooms: unsortedRooms }) => {
          const rooms = [...unsortedRooms].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          const isCollapsed = collapsedHospitals.has(hospitalId);
          const hospitalScheduleCount = rooms.reduce(
            (sum, room) => sum + getSchedulesForRoom(room.operatingRoomId).length, 0
          );

          return (
            <div key={hospitalId} className="sidebar-hospital-node">
              <div
                className="sidebar-hospital-header"
                onClick={() => toggleHospital(hospitalId)}
              >
                <span className="sidebar-chevron">{isCollapsed ? '\u25B6' : '\u25BC'}</span>
                <span className="sidebar-hospital-name">{getHospitalName(hospitalId)}</span>
                {hospitalScheduleCount > 0 && (
                  <span className="sidebar-badge">{hospitalScheduleCount}</span>
                )}
                {viewMode === 'active' && onToggleForcedHospital && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleForcedHospital(hospitalId);
                    }}
                    title={(forcedActiveHospitalIds?.has(hospitalId) ?? false)
                      ? 'Remove this hospital’s empty ORs from the active board'
                      : 'Add all of this hospital’s ORs to the active board (including empty ones)'}
                    style={{
                      marginLeft: 'auto',
                      fontSize: '0.75em',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      border: (forcedActiveHospitalIds?.has(hospitalId) ?? false) ? '1px solid rgba(22, 163, 74, 0.5)' : '1px solid var(--border-soft)',
                      backgroundColor: (forcedActiveHospitalIds?.has(hospitalId) ?? false) ? 'var(--success-soft)' : 'var(--bg-input)',
                      color: (forcedActiveHospitalIds?.has(hospitalId) ?? false) ? 'var(--success-text)' : 'var(--text-secondary)',
                      fontWeight: 500,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {(forcedActiveHospitalIds?.has(hospitalId) ?? false) ? '✓ Showing ORs' : '+ Show ORs'}
                  </button>
                )}
              </div>

              {!isCollapsed && (
                <ul className="sidebar-or-list">
                  {rooms.map(room => {
                    const scheduleCount = getSchedulesForRoom(room.operatingRoomId).length;
                    return (
                      <li
                        key={room.operatingRoomId}
                        className="sidebar-or-item"
                        onClick={() => onScrollToRoom(room.operatingRoomId)}
                        title={`Click to scroll to ${room.name}`}
                      >
                        <span className="sidebar-or-name">{room.name}</span>
                        {scheduleCount > 0 && (
                          <span className="sidebar-badge">{scheduleCount}</span>
                        )}
                        {isAdmin && (
                          <button
                            className="sidebar-create-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCreateSchedule(room.operatingRoomId);
                            }}
                            title="Create schedule for this room"
                          >
                            +
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HospitalORSidebar;
